import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Check, X, AlertCircle, RefreshCw, Plus, Eye, EyeOff } from 'lucide-react';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import { useAppSettings } from '../contexts/AppSettingsContext';
import binanceLogo from '../assets/logos/exchanges/binance.jpg';
import bybitLogo from '../assets/logos/exchanges/bybit.jpg';
import gateioLogo from '../assets/logos/exchanges/gateio.jpg';
import mexcLogo from '../assets/logos/exchanges/mexc.jpg';
import indodaxLogo from '../assets/logos/exchanges/indodax.jpg';
import { csrfFetch } from '../utils/csrfFetch';
import { buildApiUrl } from '../config';

const ExchangeSettings = () => {
  const navigate = useNavigate();
  const [apiCredentials, setApiCredentials] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showTwoFaDialog, setShowTwoFaDialog] = useState(false);
  
  const [exchanges, setExchanges] = useState([
    // International exchanges (sorted first)
    {
      id: 'binance',
      name: 'Binance',
      status: 'disconnected',
      logo: binanceLogo,
      lastConnected: null,
      international: true,
    },
    {
      id: 'bybit',
      name: 'Bybit',
      status: 'disconnected',
      logo: bybitLogo,
      lastConnected: null,
      international: true,
    },
    {
      id: 'gate',
      name: 'Gate.io',
      status: 'disconnected',
      logo: gateioLogo,
      lastConnected: null,
      international: true,
    },
    {
      id: 'mexc',
      name: 'MEXC',
      status: 'disconnected',
      logo: mexcLogo,
      lastConnected: null,
      international: true,
    },
    // Local exchange
    {
      id: 'indodax',
      name: 'Indodax',
      status: 'disconnected',
      logo: indodaxLogo,
      lastConnected: null,
      international: false,
    },
  ]);

  const [expandedExchange, setExpandedExchange] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [disconnectingExchange, setDisconnectingExchange] = useState(null);

  const fetchTwoFactorStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await csrfFetch('/account/manage/2fa', {
        method: 'GET',
        headers: { accept: 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const enabled = !!data.isTwoFactorEnabled;
        setTwoFactorEnabled(enabled);
        return enabled;
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setStatusLoading(false);
    }
    return twoFactorEnabled;
  };
  

  // Fetch connected exchanges from API
  const fetchConnectedExchanges = async () => {
    try {
      const response = await fetch(buildApiUrl('/exchange/connections'), {
        method: 'GET',
        headers: {
          'accept': '*/*'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update exchanges status based on API response
        setExchanges(prevExchanges => 
          prevExchanges.map(exchange => {
            const connectedExchange = data.find(connectedEx => 
              connectedEx.exchange && 
              connectedEx.exchange.toLowerCase() === exchange.name.toLowerCase()
            );
            
            const isConnected = !!connectedExchange;
            
            return {
              ...exchange,
              status: isConnected ? 'connected' : 'disconnected',
              lastConnected: isConnected ? connectedExchange.createdAt : exchange.lastConnected
            };
          })
        );
      }
    } catch (error) {
      console.error('Failed to fetch connected exchanges:', error);
    }
  };

  // Fetch connected exchanges on component mount
  useEffect(() => {
    fetchConnectedExchanges();
    fetchTwoFactorStatus();
  }, []);

  const handleCredentialChange = (exchangeId, field, value) => {
    setApiCredentials(prev => ({
      ...prev,
      [exchangeId]: {
        ...prev[exchangeId],
        [field]: value
      }
    }));
  };

  const handleConnect = async (exchangeId) => {
    // Use locally provided credentials instead of appSettings
    const creds = apiCredentials[exchangeId] || {};
    if (!creds.apiKey || !creds.secretKey) {
      toast.error('Please enter both API Key and Secret Key before connecting');
      return;
    }

    setTestingConnection(exchangeId);

    const isTwoFactorOn = await fetchTwoFactorStatus();
    if (!isTwoFactorOn) {
      setTestingConnection(null);
      setShowTwoFaDialog(true);
      toast.error('Setup 2FA at profile settings');
      return;
    }
    
    if (exchangeId === 'indodax') {
        // Call backend API to connect to Indodax
        const response = await csrfFetch('/exchange/indodax/connect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            apiKey: creds.apiKey,
            secretKey: creds.secretKey
          })
        });
        
        if (response.status === 200) {
          
          toast.success(`${exchanges.find(e => e.id === exchangeId).name} connected successfully!`);
          setApiCredentials(prev => ({
            ...prev,
            [exchangeId]: { apiKey: '', secretKey: '' }
          }));
        } else {

          toast.error(`Failed to connect to Indodax: ${response.message || 'Unknown error'}`);
        }
    } else {
      // TODO: Implement API connection for other exchanges
    }
    
    setTestingConnection(null);
  };

  const handleDisconnect = async (exchangeId) => {
    if (exchangeId !== 'indodax') return;

    setDisconnectingExchange(exchangeId);
    try {
      const response = await csrfFetch('/exchange/indodax/disconnect', {
        method: 'DELETE',
        headers: {
          'accept': '*/*'
        },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Indodax disconnected successfully');
        await fetchConnectedExchanges();
      } else {
        toast.error('Failed to disconnect Indodax');
      }
    } catch (error) {

      toast.error('Failed to disconnect Indodax');
    } finally {
      setDisconnectingExchange(null);
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'connected':
        return <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />;
      case 'disconnected':
        return <div className="h-3 w-3 bg-red-500 rounded-full" />;
      case 'error':
        return <div className="h-3 w-3 bg-red-500 rounded-full" />;
      default:
        return <div className="h-3 w-3 bg-gray-500 rounded-full" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Exchange Management</h1>
        <p className="text-gray-400">Connect and manage your exchange API credentials</p>
      </div>

      {/* Info Box */}
      <div className="glass-card p-6 bg-blue-500/5 border-blue-500/20">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-white mb-1">API Key Security</h3>
            <p className="text-sm text-gray-400">
              Only use API keys with trading permissions enabled. Never share your API secrets with anyone. 
              We recommend using IP whitelisting on your exchange accounts for additional security.
            </p>
          </div>
        </div>
      </div>

      {/* Exchange List */}
      <div className="space-y-3">
        {[...exchanges]
          .sort((a, b) => {
            // Sort by connection status (connected first)
            if (a.status === 'connected' && b.status !== 'connected') return -1;
            if (a.status !== 'connected' && b.status === 'connected') return 1;
            return 0;
          })
          .map((exchange) => (
          <div key={exchange.id} className="glass-card overflow-hidden">
            {/* Exchange Header */}
            <div
              className="p-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
              onClick={() => setExpandedExchange(
                expandedExchange === exchange.id ? null : exchange.id
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getStatusDot(exchange.status)}
                  <img 
                    src={exchange.logo} 
                    alt={exchange.name}
                    className="h-10 w-10 object-cover rounded-full"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${exchange.name}&background=1f2937&color=fff&size=32`;
                    }}
                  />
                  <span className="text-white font-medium w-16">{exchange.name}</span>
                  <div className="flex items-center space-x-2">
                    {exchange.international && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white">
                        International
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className={cn(
                  'h-5 w-5 text-gray-400 transition-transform',
                  expandedExchange === exchange.id ? 'rotate-90' : ''
                )} />
              </div>
            </div>

            {/* Exchange Details */}
            {expandedExchange === exchange.id && (
              <div className="px-4 pb-4 border-t border-gray-800">
                <div className="pt-4 space-y-4">
                  {exchange.status === 'connected' ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-400 mb-1">API Key</p>
                          <p className="text-white font-mono">••••••••••••••••</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400 mb-1">First Connected at</p>
                          <p className="text-white">
                            {exchange.lastConnected
                              ? new Date(exchange.lastConnected).toLocaleString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => handleDisconnect(exchange.id)}
                          disabled={disconnectingExchange === exchange.id}
                          className="btn-danger"
                        >
                          {disconnectingExchange === exchange.id ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="py-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            API Key
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={apiCredentials[exchange.id]?.apiKey || ''}
                              onChange={(e) => handleCredentialChange(exchange.id, 'apiKey', e.target.value)}
                              placeholder="Enter your API key"
                              className={cn(
                                "w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              )}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Secret Key
                          </label>
                          <div className="relative">
                            <input
                              type={showSecrets[exchange.id] ? 'text' : 'password'}
                              value={apiCredentials[exchange.id]?.secretKey || ''}
                              onChange={(e) => handleCredentialChange(exchange.id, 'secretKey', e.target.value)}
                              placeholder="Enter your secret key"
                              className={cn(
                                "w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                                "pr-10"
                              )}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                              <button
                                type="button"
                                onClick={() => setShowSecrets(prev => ({ ...prev, [exchange.id]: !prev[exchange.id] }))}
                                className="flex items-center"
                              >
                                {showSecrets[exchange.id] ? (
                                  <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-300" />
                                ) : (
                                  <Eye className="h-4 w-4 text-gray-400 hover:text-gray-300" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleConnect(exchange.id)}
                          disabled={testingConnection === exchange.id || statusLoading}
                          className="btn-primary flex items-center"
                        >
                          {testingConnection === exchange.id ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Connect
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {showTwoFaDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-800">
              <div>
                <p className="text-sm uppercase tracking-wide text-blue-400">Security</p>
                <h3 className="text-xl font-semibold text-white">Enable Two-Factor Authentication</h3>
              </div>
              <button
                onClick={() => setShowTwoFaDialog(false)}
                className="text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-300">
                Setup 2FA at profile settings before connecting your exchange API credentials.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowTwoFaDialog(false)}
                  className="btn-secondary text-sm"
                >
                  Maybe later
                </button>
                <button
                  onClick={() => {
                    setShowTwoFaDialog(false);
                    navigate('/settings/profile');
                  }}
                  className="btn-primary text-sm"
                >
                  Go to Profile Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeSettings;
